// src/studio/components/MermaidDiagram.jsx
//
// Renders one Mermaid diagram inline for a fenced ```mermaid code block
// inside a generated section (er_diagram, programs[].process_flow_diagram,
// requirement_designs[].design_visual, ...) -- MarkdownView routes such a
// block here instead of showing it as plain preformatted source.
//
// Deliberately minimal compared to codebase/components/MermaidViewer.jsx
// (no zoom/export toolbar, no expand modal) -- that component solves a
// different need, an interactive architecture-diagram explorer; this one
// is "show the diagram" inside a document section's read-only view, kept
// as lightweight as MarkdownView's own stated philosophy.
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import mermaid from "mermaid";

// fontFamily/fontSize match the surrounding section text (MarkdownView's
// own prose is text-sm/14px in the app's Poppins font-sans, see
// frontend/src/index.css's --font-sans) -- Mermaid's own default theme
// otherwise renders noticeably larger, mismatched text inside the diagram,
// standing out from the rest of the field it's embedded in.
//
// useMaxWidth: false matters just as much as the fontSize above: Mermaid's
// default (true) stamps width="100%" onto the SVG so it stretches to fill
// whatever container it's dropped into, regardless of how few/short its
// nodes are -- a two-node diagram then gets blown up to the full section
// width, and every font-size along with it (the 14px above is a real SVG
// unit, but a 2x-stretched SVG renders it at ~28px on screen). Disabling
// this makes Mermaid lay the SVG out at its own natural size instead, so
// the CSS below only ever needs to cap growth for a genuinely large
// diagram, never scale up a small one.
// suppressErrorRendering matters as much as the try/catch below does: by
// default mermaid.render() does NOT reject its promise on a syntax error --
// it resolves successfully with Mermaid's own built-in error diagram (the
// "bomb" icon / "Syntax error in text") *as* the svg, so the .catch() branch
// below would never run and that bomb graphic would render in place of our
// readable raw-source fallback. Setting this makes render() actually throw
// on invalid syntax instead, which is what the .catch() below expects.
mermaid.initialize({
  startOnLoad: false,
  securityLevel: "loose",
  theme: "default",
  suppressErrorRendering: true,
  themeVariables: {
    fontFamily: '"Poppins", sans-serif',
    fontSize: "10.5px",
  },
  flowchart: { useMaxWidth: false },
  sequence: { useMaxWidth: false },
  er: { useMaxWidth: false },
});

// Same source sanitisation as MermaidViewer.jsx: strip anything that could
// execute script via an injected node label *before* Mermaid ever sees the
// source, since its SVG output is rendered as-is via dangerouslySetInnerHTML.
function sanitize(code) {
  return (code || "")
    .replace(/<\s*\/?\s*(script|iframe|object|embed|applet|form)\b[^>]*>/gi, "")
    .replace(/\bon[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/javascript\s*:/gi, "")
    .replace(/data\s*:\s*text\/html/gi, "");
}

// The LLM writes node/edge labels as free prose ("Opportunity Record w/
// Opportunity.Primary_Product__c mapped", "OpportunityScoringService
// (async)", "assign owner, set Status"), and Mermaid's flowchart grammar
// treats several characters inside an *unquoted* [label] or |label| as
// syntax -- parentheses in particular open a nested node/edge shape,
// which is exactly what silently broke real diagrams from this pipeline
// (a node label containing "(async)" failed to parse). Quoting is always
// valid whether or not a label actually needs it, so auto-quoting every
// not-already-quoted label is a strict safety net, not a behaviour change
// for labels that were already parsing fine. Flowcharts only: an
// erDiagram's `||--o{` and a stateDiagram's `[*]` are syntax, not labels.
const FLOWCHART_HEADER_RE = /^\s*(?:%%[^\n]*\n\s*)*(?:flowchart|graph)\b/;

function autoQuoteLabels(code) {
  if (!FLOWCHART_HEADER_RE.test(code)) return code;
  // The character class excludes '"' from what it captures, so a label
  // that's already quoted (or contains a literal quote) is left alone --
  // only a genuinely bare, unquoted label gets wrapped.
  return code
    .replace(/\[([^[\]"\n]+)\]/g, (_match, inner) => `["${inner.trim()}"]`)
    .replace(/\|([^|"\n]+)\|/g, (_match, inner) => `|"${inner.trim()}"|`);
}

// Mistakes the LLM makes that Mermaid cannot parse. Flowcharts: "A --|x| B" /
// "A --|x|--> B" (no arrowhead), "B <--|x| A" (no left arrows) and "A -->{}B".
// Sequence diagrams: a ';' in message text ends the statement. Same repairs as
// the export's mermaid_diagram_images.py; keep the two in sync.
const SEQUENCE_HEADER_RE = /^\s*(?:%%[^\n]*\n\s*)*sequenceDiagram\b/;
const LEFT_LINK_LINE_RE =
  /^(\s*)(\S.*?)\s*<(-{2,}|-\.+-|={2,})(?!>)\s*(\|[^|\n]*\|)?\s*(\S.*?)\s*$/;
const LINK_TOKEN_RE = /-{2,}|={2,}|-\.+-/g;

function flipLeftLink(line) {
  const match = LEFT_LINK_LINE_RE.exec(line);
  if (!match || (line.match(LINK_TOKEN_RE) || []).length !== 1) return line;
  const [, indent, target, link, label, source] = match;
  return `${indent}${source} ${link}>${label || ""} ${target}`;
}

function repairCommonMistakes(code) {
  if (SEQUENCE_HEADER_RE.test(code)) {
    return code
      .split("\n")
      .map((line) => {
        const colon = line.indexOf(":");
        return colon < 0 ? line : line.slice(0, colon + 1) + line.slice(colon + 1).replace(/;/g, ",");
      })
      .join("\n");
  }
  if (!FLOWCHART_HEADER_RE.test(code)) return code;
  return code
    .replace(/(?<![-.=<>])--\|([^|\n]*)\|(?:\s*-->)?/g, (_match, label) => `-->|${label}|`)
    .replace(/(-->|-\.->|==>)\s*\{\}/g, (_match, link) => `${link} `)
    .split("\n")
    .map(flipLeftLink)
    .join("\n");
}

export default function MermaidDiagram({ code }) {
  const displayCode = useMemo(() => sanitize(code).trim(), [code]);
  const safeCode = useMemo(
    () => autoQuoteLabels(repairCommonMistakes(displayCode)),
    [displayCode]
  );
  // Keyed by the code it was computed for, rather than reset synchronously
  // at the top of the effect -- a result whose forCode no longer matches
  // safeCode (a stale render from a previous `code` prop) is treated as
  // "still loading" below, without ever calling setState outside the
  // async callbacks themselves.
  const [result, setResult] = useState({ forCode: null, svg: "", error: "" });

  useEffect(() => {
    if (!safeCode) return undefined;
    let cancelled = false;

    mermaid
      .render(`studio-mmd-${Math.random().toString(36).slice(2)}`, safeCode)
      .then((out) => {
        if (!cancelled)
          setResult({ forCode: safeCode, svg: out.svg || "", error: "" });
      })
      .catch((e) => {
        if (!cancelled)
          setResult({
            forCode: safeCode,
            svg: "",
            error: String(e?.message || e),
          });
      });

    return () => {
      cancelled = true;
    };
  }, [safeCode]);

  if (!safeCode) return null;

  const { svg, error } =
    result.forCode === safeCode ? result : { svg: "", error: "" };

  if (error) {
    // Falls back to the readable (pre-auto-quote) source, same as the
    // export pipeline's own "degrade, don't fail" behaviour for a diagram
    // that won't render.
    return (
      <pre className="bg-gray-900 text-emerald-400 rounded-xl p-4 text-xs font-mono overflow-x-auto my-3 whitespace-pre">
        {displayCode}
      </pre>
    );
  }

  if (!svg) {
    return (
      <div className="text-xs text-slate-400 italic my-3">
        Rendering diagram…
      </div>
    );
  }

  return (
    <div
      className="my-3 rounded-xl border border-gray-200 bg-white p-3 overflow-x-auto [&_svg]:max-w-full [&_svg]:h-auto"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

MermaidDiagram.propTypes = {
  code: PropTypes.string,
};
